import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { Cart } from '../../../core/models/cart.model';
import { Product } from '../../../core/models/product.model';
import { AuthService } from '../../../core/services/auth.service';
import { MediaImageCacheService } from '../../../core/services/media-image-cache.service';
import { ProductsService } from '../../../core/services/products.service';
import { TranslatePipe } from '../../../core/pipes/t.pipe';
import { ProductRailComponent } from '../product-rail/product-rail.component';
import {
  collectCartCategoryIds,
  mergeRecommendedProducts,
} from './recommended-products.util';

@Component({
  selector: 'app-recommended-products',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ProductRailComponent],
  templateUrl: './recommended-products.component.html',
  styleUrls: ['./recommended-products.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecommendedProductsComponent implements OnChanges, OnDestroy {
  @Input() cart: Cart | null = null;
  @Input() titleKey = 'checkout.complete_your_order';
  @Input() subtitleKey = 'checkout.related_products_subtitle';
  @Input() maxProducts = 12;

  @Output() addToCart = new EventEmitter<number>();
  @Output() buyNow = new EventEmitter<number>();
  @Output() quickPreview = new EventEmitter<Product>();

  products: Product[] = [];
  loading = false;
  animating = false;

  private readonly destroy$ = new Subject<void>();
  private lastCategoryKey = '';

  constructor(
    private productsService: ProductsService,
    private authService: AuthService,
    private mediaImageCache: MediaImageCacheService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cart']) {
      this.loadRecommendations();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByProductId = (_: number, product: Product): number => product.id;

  private loadRecommendations(): void {
    const cart = this.cart;
    if (!cart?.cartItems?.length) {
      this.products = [];
      this.lastCategoryKey = '';
      this.cdr.markForCheck();
      return;
    }

    const categoryIds = collectCartCategoryIds(cart.cartItems).slice(0, 4);
    const categoryKey = categoryIds.slice().sort((a, b) => a - b).join(',');
    if (!categoryIds.length) {
      this.products = [];
      this.lastCategoryKey = '';
      this.cdr.markForCheck();
      return;
    }

    const cartProductIds = new Set(
      cart.cartItems.map((item) => item.productId ?? item.product?.id).filter(Boolean) as number[]
    );

    if (categoryKey === this.lastCategoryKey && this.products.length) {
      this.products = this.products.filter((p) => !cartProductIds.has(p.id)).slice(0, this.maxProducts);
      this.cdr.markForCheck();
      return;
    }

    this.lastCategoryKey = categoryKey;
    this.loading = true;
    this.animating = true;
    this.cdr.markForCheck();

    const userId = this.authService.getUserId() ?? undefined;
    const requests = categoryIds.map((categoryId) =>
      this.productsService
        .getProductsByCategory(categoryId, { includeDescendants: true }, userId)
        .pipe(catchError(() => of([] as Product[])))
    );

    forkJoin(requests)
      .pipe(
        map((groups) =>
          mergeRecommendedProducts(groups, cartProductIds, this.maxProducts) as Product[]
        ),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (products) => {
          this.products = products;
          this.loading = false;
          this.mediaImageCache.warmAll(
            products.map((p) => p.imageUrl),
            'card'
          );
          this.cdr.markForCheck();
          setTimeout(() => {
            this.animating = false;
            this.cdr.markForCheck();
          }, 450);
        },
        error: () => {
          this.products = [];
          this.loading = false;
          this.animating = false;
          this.cdr.markForCheck();
        },
      });
  }
}
